import { ReactNode } from "react";
import CloseIcon from "@mui/icons-material/Close";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LanguageIcon from "@mui/icons-material/Language";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import NotesIcon from "@mui/icons-material/Notes";
import { IconButton } from "@mui/material";
import DSBox from "../views/system-design/DSBox";
import DSTypography from "../views/system-design/DSTypography";
import DSChip from "../views/system-design/DSChip";

// Shared read-only view of a submission, used by both the New Artists and
// Artists Briefs submission tables. selectedBriefTitles is optional (only
// artists-briefs submissions have it).
export type SubmissionModalRow = {
  fullName?: string;
  email?: string;
  portfolioUrl?: string;
  message?: string | null;
  createdAt?: string;
  selectedBriefTitles?: string[];
};

const Row = (props: {
  icon: ReactNode;
  label: string;
  children: ReactNode;
}) => (
  <DSBox sx={{ display: "flex", gap: 1.5 }}>
    <DSBox sx={{ color: "text.secondary", display: "flex", pt: "2px" }}>
      {props.icon}
    </DSBox>
    <DSBox
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
      }}
    >
      <DSTypography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: "text.secondary",
        }}
      >
        {props.label}
      </DSTypography>
      <DSBox sx={{ fontSize: 15, wordBreak: "break-word" }}>
        {props.children}
      </DSBox>
    </DSBox>
  </DSBox>
);

const SubmissionModal = (props: {
  row?: SubmissionModalRow;
  setShowModal: (show: boolean) => void;
}) => {
  const row = props.row;
  if (!row) return null;

  const portfolio = row.portfolioUrl || "";
  const portfolioHref = /^https?:\/\//i.test(portfolio)
    ? portfolio
    : /^[a-z][a-z0-9+.-]*:/i.test(portfolio)
      ? ""
      : portfolio
        ? `https://${portfolio}`
        : "";

  const initials = (row.fullName || "?")
    .trim()
    .split(/\s+/)
    .map((word) => word[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <>
      <DSBox
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 3,
          py: 2.5,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <DSBox
          sx={{
            width: 48,
            height: 48,
            flexShrink: 0,
            borderRadius: "50%",
            bgcolor: "#ffd848",
            color: "#0c0c0c",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: 18,
          }}
        >
          {initials}
        </DSBox>
        <DSBox sx={{ flex: 1, minWidth: 0 }}>
          <DSTypography variant="h5" sx={{ m: 0 }}>
            {row.fullName || "Submission"}
          </DSTypography>
          {row.createdAt && (
            <DSTypography sx={{ fontSize: 13, color: "text.secondary" }}>
              {new Date(row.createdAt).toLocaleString()}
            </DSTypography>
          )}
        </DSBox>
        <IconButton
          onClick={() => props.setShowModal(false)}
          sx={{ alignSelf: "flex-start", mt: -0.5, mr: -1 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DSBox>

      <DSBox sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
        <Row icon={<MailOutlineIcon fontSize="small" />} label="Email">
          {row.email ? <a href={`mailto:${row.email}`}>{row.email}</a> : "—"}
        </Row>

        <Row icon={<LanguageIcon fontSize="small" />} label="Portfolio">
          {portfolio ? (
            portfolioHref ? (
              <a
                href={portfolioHref}
                target="_blank"
                rel="noreferrer"
                style={{ overflowWrap: "anywhere" }}
              >
                {portfolio}
              </a>
            ) : (
              <span style={{ overflowWrap: "anywhere" }}>{portfolio}</span>
            )
          ) : (
            "—"
          )}
        </Row>

        {row.selectedBriefTitles && row.selectedBriefTitles.length > 0 ? (
          <Row
            icon={<LocalOfferOutlinedIcon fontSize="small" />}
            label="Selected briefs"
          >
            <DSBox sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {row.selectedBriefTitles.map((title, index) => (
                <DSChip
                  key={`${title}-${index}`}
                  label={title}
                  size="small"
                  variant="outlined"
                />
              ))}
            </DSBox>
          </Row>
        ) : null}

        <Row icon={<NotesIcon fontSize="small" />} label="Message">
          <DSBox
            sx={{
              mt: 0.5,
              p: 2,
              borderRadius: 2,
              bgcolor: "paper.elevation4",
              maxHeight: 260,
              overflowY: "auto",
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              fontSize: 15,
              lineHeight: 1.5,
            }}
          >
            {row.message || "—"}
          </DSBox>
        </Row>
      </DSBox>
    </>
  );
};

export default SubmissionModal;
